import React, { useState, useEffect, useMemo } from 'react';
import './StockHistoryModal.css';
import apiService from '../../services/api';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const StockHistoryModal = ({ product, onClose }) => {
  const [locations, setLocations] = useState([]);
  const [variations, setVariations] = useState([]);
  const [selectedVariationId, setSelectedVariationId] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [stockDetails, setStockDetails] = useState(null);
  const [stockHistory, setStockHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMeta, setIsFetchingMeta] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [searchHistory, setSearchHistory] = useState('');

  const fmt = (val) => parseFloat(val || 0).toFixed(2);

  // Load product variations and locations on mount
  useEffect(() => {
    if (!product) return;
    const loadMeta = async () => {
      setIsFetchingMeta(true);
      setError('');
      try {
        const res = await apiService.getProductForStockHistory(product.id);
        const data = res.data || res;
        const vars = data.product?.variations || [];
        const locs = data.business_locations || [];
        setVariations(vars);
        setLocations(locs);
        if (vars.length > 0) setSelectedVariationId(String(vars[0].id));
        if (locs.length > 0) setSelectedLocationId(String(locs[0].id));
      } catch (e) {
        setError('Failed to load product details: ' + e.message);
      } finally {
        setIsFetchingMeta(false);
      }
    };
    loadMeta();
  }, [product]);

  // Fetch stock history whenever variation or location changes
  useEffect(() => {
    if (!product || !selectedVariationId || !selectedLocationId) return;
    const loadHistory = async () => {
      setIsLoading(true);
      setError('');
      try {
        const res = await apiService.getProductStockHistory(product.id, selectedVariationId, selectedLocationId);
        const data = res.data || res;
        setStockDetails(data.stock_details || null);
        setStockHistory(data.stock_history || []);
      } catch (e) {
        setError('Failed to load stock history: ' + e.message);
      } finally {
        setIsLoading(false);
      }
    };
    loadHistory();
  }, [product, selectedVariationId, selectedLocationId]);

  // Reset page when filters/search change
  useEffect(() => { setCurrentPage(1); }, [selectedVariationId, selectedLocationId, searchHistory, perPage]);

  const filteredHistory = useMemo(() => {
    if (!searchHistory.trim()) return stockHistory;
    const lower = searchHistory.toLowerCase();
    return stockHistory.filter(row =>
      (row.type_label || row.type || '').toLowerCase().includes(lower) ||
      (row.ref_no || '').toLowerCase().includes(lower) ||
      (row.contact_name || '').toLowerCase().includes(lower) ||
      (row.supplier_business_name || '').toLowerCase().includes(lower) ||
      (row.date || '').includes(lower)
    );
  }, [stockHistory, searchHistory]);

  const totalPages = Math.max(1, Math.ceil(filteredHistory.length / perPage));
  const pagedHistory = filteredHistory.slice((currentPage - 1) * perPage, currentPage * perPage);

  if (!product) return null;

  const unit = stockDetails?.unit || '';

  return (
    <div className="stock-history-modal-overlay" onClick={onClose}>
      <div className="stock-history-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="stock-history-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h2 style={{ margin: 0 }}>Product Stock History</h2>
            <span className="product-ref-tag">#{product.sku || product.id}</span>
          </div>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="stock-history-body">
          {/* Product Name */}
          <div style={{ marginBottom: '1rem' }}>
            <h1 style={{ fontSize: '1.15rem', fontWeight: '800', margin: '0', color: '#0f172a' }}>
              {product.name}
            </h1>
          </div>

          {/* Filters */}
          {isFetchingMeta ? (
            <div style={{ padding: '1rem', color: '#64748b' }}>Loading product details...</div>
          ) : (
            <div className="stock-history-filters">
              <div className="filter-group">
                <label>Product / Variation</label>
                <select className="filter-select" value={selectedVariationId} onChange={(e) => setSelectedVariationId(e.target.value)}>
                  {variations.map(v => (
                    <option key={v.id} value={v.id}>
                      {product.name}{v.name && v.name !== 'DUMMY' ? ` - ${v.name}` : ''} ({v.sub_sku})
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label>Business Location</label>
                <select className="filter-select" value={selectedLocationId} onChange={(e) => setSelectedLocationId(e.target.value)}>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {error && (
            <div style={{ padding: '0.75rem 1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', marginBottom: '1rem', fontSize: '0.875rem' }}>
              {error}
            </div>
          )}

          {isLoading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading stock history...</div>
          ) : stockDetails && (
            <>
              {/* Statistics Grid */}
              <div className="stock-history-stats">
                <div className="stats-column column-in">
                  <h3>Quantities In</h3>
                  <div className="stat-item"><span className="stat-label">Total Purchase</span><span className="stat-value">{fmt(stockDetails.total_purchase)} {unit}</span></div>
                  <div className="stat-item"><span className="stat-label">Opening Stock</span><span className="stat-value">{fmt(stockDetails.total_opening_stock)} {unit}</span></div>
                  <div className="stat-item"><span className="stat-label">Total Sell Return</span><span className="stat-value">{fmt(stockDetails.total_sell_return)} {unit}</span></div>
                  <div className="stat-item"><span className="stat-label">Stock Transfers (In)</span><span className="stat-value">{fmt(stockDetails.total_purchase_transfer)} {unit}</span></div>
                </div>
                <div className="stats-column column-out">
                  <h3>Quantities Out</h3>
                  <div className="stat-item"><span className="stat-label">Total Sold</span><span className="stat-value">{fmt(stockDetails.total_sold)} {unit}</span></div>
                  <div className="stat-item"><span className="stat-label">Total Stock Adjustment</span><span className="stat-value">{fmt(stockDetails.total_adjusted)} {unit}</span></div>
                  <div className="stat-item"><span className="stat-label">Total Purchase Return</span><span className="stat-value">{fmt(stockDetails.total_purchase_return)} {unit}</span></div>
                  <div className="stat-item"><span className="stat-label">Stock Transfers (Out)</span><span className="stat-value">{fmt(stockDetails.total_sell_transfer)} {unit}</span></div>
                </div>
                <div className="stats-column column-totals">
                  <h3>Totals</h3>
                  <div className="stat-item total-highlight">
                    <span className="stat-label">Current stock</span>
                    <span className="stat-value">{fmt(stockDetails.current_stock)} {unit}</span>
                  </div>
                </div>
              </div>

              {/* History Table */}
              <div className="history-table-section">
                {/* Table controls */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Show</span>
                    <select
                      value={perPage}
                      onChange={(e) => setPerPage(Number(e.target.value))}
                      style={{ padding: '0.3rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', background: '#fff' }}
                    >
                      {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span style={{ fontSize: '0.85rem', color: '#64748b' }}>entries</span>
                  </div>
                  <input
                    type="text"
                    placeholder="Search history..."
                    value={searchHistory}
                    onChange={(e) => setSearchHistory(e.target.value)}
                    style={{ padding: '0.35rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', width: '200px', outline: 'none' }}
                  />
                </div>

                <div className="history-table-container">
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Quantity Change</th>
                        <th>New Quantity</th>
                        <th>Date</th>
                        <th>Reference No</th>
                        <th>Customer/Supplier Info</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedHistory.length === 0 ? (
                        <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: '1.5rem' }}>No records found.</td></tr>
                      ) : pagedHistory.map((row, idx) => {
                        const change = parseFloat(row.quantity_change || 0);
                        const isPositive = change >= 0;
                        const contactInfo = row.supplier_business_name
                          ? `${row.supplier_business_name}${row.contact_name ? ', ' + row.contact_name : ''}`
                          : (row.contact_name || '');
                        return (
                          <tr key={idx}>
                            <td><span className={`badge-type ${(row.type || '').toLowerCase().replace(/_/g, '-')}`}>{row.type_label || row.type}</span></td>
                            <td className={`stock-change ${isPositive ? 'positive' : 'negative'}`}>{isPositive ? '+' : ''}{fmt(change)} {unit}</td>
                            <td>{fmt(row.stock)} {unit}</td>
                            <td>{(row.date || '').substring(0, 16)}</td>
                            <td style={{ fontWeight: '600' }}>{row.ref_no || 'N/A'}</td>
                            <td style={{ color: '#64748b' }}>{contactInfo || 'N/A'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                    Showing {filteredHistory.length === 0 ? 0 : (currentPage - 1) * perPage + 1} to {Math.min(currentPage * perPage, filteredHistory.length)} of {filteredHistory.length} entries
                    {searchHistory && ` (filtered from ${stockHistory.length} total)`}
                  </span>
                  <div style={{ display: 'flex', gap: '0.35rem' }}>
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      style={{ padding: '0.3rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '5px', background: currentPage === 1 ? '#f1f5f9' : '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: '0.82rem' }}
                    >«</button>
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      style={{ padding: '0.3rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '5px', background: currentPage === 1 ? '#f1f5f9' : '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: '0.82rem' }}
                    >Previous</button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let page;
                      if (totalPages <= 5) page = i + 1;
                      else if (currentPage <= 3) page = i + 1;
                      else if (currentPage >= totalPages - 2) page = totalPages - 4 + i;
                      else page = currentPage - 2 + i;
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          style={{ padding: '0.3rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '5px', background: currentPage === page ? '#6366f1' : '#fff', color: currentPage === page ? '#fff' : '#374151', cursor: 'pointer', fontSize: '0.82rem', fontWeight: currentPage === page ? '700' : '400' }}
                        >{page}</button>
                      );
                    })}
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      style={{ padding: '0.3rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '5px', background: currentPage === totalPages ? '#f1f5f9' : '#fff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: '0.82rem' }}
                    >Next</button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      style={{ padding: '0.3rem 0.6rem', border: '1px solid #d1d5db', borderRadius: '5px', background: currentPage === totalPages ? '#f1f5f9' : '#fff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: '0.82rem' }}
                    >»</button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StockHistoryModal;
